import imageCompression from 'browser-image-compression';

// Compression settings for feed images (gallery grid)
const FEED_SETTINGS = {
  maxWidthOrHeight: 1280,
  initialQuality: 0.75,
  useWebWorker: true,
  maxSizeMB: 1.5, // Safety limit
  fileType: 'image/jpeg',
};

// Compression settings for full-size images (lightbox)
const FULL_SETTINGS = {
  maxWidthOrHeight: 1920,
  initialQuality: 0.85,
  useWebWorker: true,
  maxSizeMB: 2, // Safety limit
  fileType: 'image/jpeg',
};

/**
 * Compress an image file to feed size (1,280px @ 0.75 quality)
 * @param {File} file - Original image file
 * @returns {Promise<File>} - Compressed file
 */
export const compressImageForFeed = async (file) => {
  try {
    const options = {
      ...FEED_SETTINGS,
    };

    // Check if compression is needed
    const needsCompression = await shouldCompress(file, FEED_SETTINGS.maxWidthOrHeight);
    
    if (!needsCompression && file.size <= FEED_SETTINGS.maxSizeMB * 1024 * 1024) {
      // If already small enough, return original but ensure it's JPEG
      if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
        return file;
      }
      // Convert non-JPEG to JPEG
      return await imageCompression(file, options);
    }

    const compressedFile = await imageCompression(file, options);
    console.log(`Feed compression: ${(file.size / 1024).toFixed(1)}KB → ${(compressedFile.size / 1024).toFixed(1)}KB`);
    return compressedFile;
  } catch (error) {
    console.error('Feed compression error:', error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Compress an image file to full size (1,920px @ 0.85 quality)
 * @param {File} file - Original image file
 * @returns {Promise<File>} - Compressed file
 */
export const compressImageForFull = async (file) => {
  try {
    const options = {
      ...FULL_SETTINGS,
    };

    // Check if compression is needed
    const needsCompression = await shouldCompress(file, FULL_SETTINGS.maxWidthOrHeight);
    
    if (!needsCompression && file.size <= FULL_SETTINGS.maxSizeMB * 1024 * 1024) {
      // If already small enough, return original but ensure it's JPEG
      if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
        return file;
      }
      // Convert non-JPEG to JPEG
      return await imageCompression(file, options);
    }

    const compressedFile = await imageCompression(file, options);
    console.log(`Full compression: ${(file.size / 1024).toFixed(1)}KB → ${(compressedFile.size / 1024).toFixed(1)}KB`);
    return compressedFile;
  } catch (error) {
    console.error('Full compression error:', error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Compress image to both feed and full sizes
 * @param {File} file - Original image file
 * @returns {Promise<{feedFile: File, fullFile: File}>} - Both compressed versions
 */
export const compressImageBothSizes = async (file) => {
  try {
    // Compress both sizes in parallel for better performance
    const [feedFile, fullFile] = await Promise.all([
      compressImageForFeed(file),
      compressImageForFull(file),
    ]);

    return { feedFile, fullFile };
  } catch (error) {
    console.error('Compression error:', error);
    // Return original file for both if compression fails
    return {
      feedFile: file,
      fullFile: file,
    };
  }
};

/**
 * Check if an image needs compression based on dimensions
 * @param {File} file - Image file
 * @param {number} maxDimension - Maximum dimension (width or height)
 * @returns {Promise<boolean>} - Whether compression is needed
 */
const shouldCompress = async (file, maxDimension) => {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      const needsCompression = img.width > maxDimension || img.height > maxDimension;
      resolve(needsCompression);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // If we can't read dimensions, assume compression is needed
      resolve(true);
    };
    
    img.src = url;
  });
};

